import { Component, OnInit, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterModule, Router, NavigationEnd } from '@angular/router';
import { Logo } from '../../components/logo/logo';
import { Modal } from '../../components/modal/modal';
import { BookingModal } from '../../components/booking-modal/booking-modal';
import { UserService } from '../../services/user.service';
import { AdminHeader } from '../../services/admin-header';
import { AdminBookingModalService } from '../../services/admin-booking-modal.service';
import { filter } from 'rxjs/operators';

@Component({
	selector: 'app-admin-layout',
	imports: [CommonModule, RouterModule, Logo, Modal, BookingModal],
	templateUrl: './admin-layout.html',
	styleUrl: './admin-layout.css'
})
export class AdminLayout implements OnInit {
	currentRoute = '';
	isAnimating = signal(false);
	showSidebar = signal(false);
	showContent = signal(false);
	isLoggingOut = signal(false);
	mobileMenuOpen = signal(false);

	constructor(
		private userService: UserService,
		private router: Router,
		protected adminHeaderService: AdminHeader,
		protected bookingModalService: AdminBookingModalService
	) {
		// Track route changes and close mobile menu
		this.router.events.pipe(
			filter(event => event instanceof NavigationEnd)
		).subscribe((event: any) => {
			this.currentRoute = event.url;
			this.mobileMenuOpen.set(false); // Close mobile menu on navigation
		});
	}

	ngOnInit() {
		this.currentRoute = this.router.url;
		// Trigger sidebar fade-in animation
		setTimeout(() => {
			this.showSidebar.set(true);
		}, 50);
		// Show main content after sidebar animation completes
		setTimeout(() => {
			this.showContent.set(true);
		}, 750); // 50ms + 600ms animation + 100ms buffer
	}

	getUserName(): string {
		const user = this.userService.userDetails();
		if (user?.first_name && user?.last_name) {
			return `${user.first_name} ${user.last_name}`;
		}
		return user?.email?.split('@')[0] || 'Admin';
	}

	getUserInitials(): string {
		const user = this.userService.userDetails();
		if (user?.first_name && user?.last_name) {
			return `${user.first_name.charAt(0)}${user.last_name.charAt(0)}`.toUpperCase();
		}
		const name = this.getUserName();
		return name.substring(0, 2).toUpperCase();
	}

	getPageTitle(): string {
		if (this.currentRoute.includes('/admin/dashboard')) {
			return 'Dashboard';
		} else if (this.currentRoute.includes('/admin/services')) {
			return 'Services Management';
		} else if (this.currentRoute.includes('/admin/blogs/add')) {
			return 'Create New Post';
		} else if (this.currentRoute.includes('/admin/blogs')) {
			return 'Blog Management';
		}
		return 'Admin';
	}

	getPageSubtitle(): string {
		if (this.currentRoute.includes('/admin/dashboard')) {
			return 'Welcome back! Here\'s what\'s happening today';
		} else if (this.currentRoute.includes('/admin/services')) {
			return 'Manage your hair studio services';
		} else if (this.currentRoute.includes('/admin/blogs/add')) {
			return 'Share your latest updates and insights';
		} else if (this.currentRoute.includes('/admin/blogs')) {
			return 'Create and manage your blog posts';
		}
		return '';
	}

	toggleMobileMenu() {
		this.mobileMenuOpen.set(!this.mobileMenuOpen());
	}

	logout() {
		// Step 1: Show spinner
		this.isLoggingOut.set(true);

		// Step 2: Call logout API
		this.userService.logout$().subscribe({
			next: () => {
				// API success - proceed with animation
				this.startLogoutAnimation();
			},
			error: () => {
				// API failed - clear storage and proceed anyway
				localStorage.removeItem('authToken');
				this.startLogoutAnimation();
			}
		});
	}

	startLogoutAnimation() {
		// Step 3: Hide everything at once (sidebar elements + content + logo)
		this.isAnimating.set(true);

		// Step 4: After elements fade out, do width animation and navigate
		setTimeout(() => {
			this.userService.clearUserDataAndRedirect();
		}, 1500);
	}
}
